// src/lib/bet-load.js -- #1554 (brief: docs/audits/2026-10-17-1554-paged-bet-load-brief.md, rev 2).
//
// Single job: read every bet of one signed-in user, with all of its legs, through supabase-js --
// or throw. It never returns a partial set.
// Reads: `bets` with `bet_legs` embedded (RLS scopes both to the caller), in keyset pages on `id`.
// Writes: nothing. Reports nothing: the caller owns severity (CC-IMPL-037).
// Does NOT: map rows (storage.js rowToBet does), compare ids in JS (Ultron G5), or know about React.
// No imports, so a Node script (scripts/qa/paged-bet-load-staging-proof.mjs) can run it as-is.
//
// Why embedded legs (option A): each page is one PostgREST request -- one statement, one snapshot --
// and a bet's legs come from its own LEFT JOIN LATERAL, so no page boundary can split them, and the
// leg read never puts bet ids in a URL (the gateway rejects URLs over ~25 KB). PostgREST applies
// max-rows to every plan node, so an embed is capped PER BET (Q1); a bet has at most 25 legs.

export const PAGE_SIZE = 250;           // always <= 1000, the lowest max-rows in any environment
export const MAX_PAGES = 100;           // 25,000 bets: past this the load fails loudly (G9 residual)
export const BETS_SEED_CHUNK = 200;     // useBetsRealtime seed: <= 1 row per id, ~4 KB of ids
export const LEGS_SEED_CHUNK = 39;      // useBetLegsRealtime seed: 39 x 25 legs = 975 < 1000 (G2)
export const LEGS_SEED_ROW_CAP = 1000;  // a legs-seed chunk this large may have been cut by max-rows

// Every reason code a load or seed failure can carry. All match TAG_RE (analytics-events.js), so the
// PostHog half of errorReporting sends them as `stage`.
export const LOAD_REASONS = Object.freeze([
  "page_error", "embed_missing", "non_array", "row_shape", "cursor_stuck", "legs_shape",
  "truncated", "cancelled", "mapper", "seed_cap",
]);

// PostgREST: no relationship / ambiguous relationship in the schema cache (G8). Under option A this
// takes the whole bet list down, so it gets its own name in Sentry and PostHog.
const EMBED_MISSING_CODES = new Set(["PGRST200", "PGRST201"]);

export class BetLoadError extends Error {
  constructor(reason, message, { code, cause } = {}) {
    super(message);
    this.name = "BetLoadError";
    this.reason = reason;
    if (code !== undefined) this.code = code;
    if (cause !== undefined) this.cause = cause;
  }
}

// The reason code for any error a load path caught; never throws.
export function loadFailureReason(err) {
  return err instanceof BetLoadError ? err.reason : "unexpected";
}

// True when the load stopped because a newer load or a subject change superseded it.
export function isLoadCancelled(err) {
  return err instanceof BetLoadError && err.reason === "cancelled";
}

// Split `items` into consecutive arrays of at most `size`. Does not mutate `items`.
export function chunk(items, size) {
  if (!Number.isInteger(size) || size < 1) throw new RangeError(`chunk: size must be a positive integer, got ${size}`);
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

function pageQuery(supabase, userId, cursor, pageSize) {
  let q = supabase.from("bets").select("*, bet_legs(*)").eq("user_id", userId);
  if (cursor !== null) q = q.gt("id", cursor);
  return q
    .order("id", { ascending: true })
    .order("leg_index", { referencedTable: "bet_legs", ascending: true })
    .limit(pageSize);
}

// The row's legs, checked and sorted by leg_index. rowToBet maps by array position and
// applyLegPatches patches by index, so the order is load-bearing (G1): the server is asked for it
// AND the client sorts, so neither alone is trusted.
function sortedLegs(row) {
  const legs = row.bet_legs;
  if (!Array.isArray(legs)) throw new BetLoadError("legs_shape", `bet ${row.id}: bet_legs is not an array`);
  for (const leg of legs) {
    if (!leg || typeof leg !== "object" || !Number.isInteger(leg.leg_index)) {
      throw new BetLoadError("legs_shape", `bet ${row.id}: a leg has no integer leg_index`);
    }
  }
  return [...legs].sort((a, b) => a.leg_index - b.leg_index);
}

function createdAtKey(row) {
  const t = row.created_at == null ? NaN : Date.parse(row.created_at);
  return Number.isNaN(t) ? Infinity : t; // NULL / unparseable first, as Postgres DESC (NULLS FIRST)
}

// Today's display order: created_at desc. Ties fall back to the REVERSE of the server's id-asc
// arrival order (= id desc in the server's collation), so ids are never compared in JS.
function displayOrder(rows) {
  return rows
    .map((row, i) => ({ row, i, t: createdAtKey(row) }))
    .sort((a, b) => (a.t !== b.t ? (b.t > a.t ? 1 : -1) : b.i - a.i))
    .map((x) => x.row);
}

/**
 * Read every bet of `userId` with its legs.
 * @param {object} supabase  supabase-js client carrying the user's session
 * @param {string} userId
 * @param {object} [opts]
 * @param {() => boolean} [opts.isCurrent]  checked before and after every page; false -> throws `cancelled`
 * @returns {Promise<object[]>} raw bet rows, each with a `bet_legs` array sorted by leg_index
 * @throws {BetLoadError} reason in LOAD_REASONS
 */
export async function loadUserBets(supabase, userId, { isCurrent = () => true, pageSize = PAGE_SIZE, maxPages = MAX_PAGES } = {}) {
  const assertCurrent = () => {
    if (!isCurrent()) throw new BetLoadError("cancelled", "bet load superseded");
  };
  const seen = new Set();
  const rows = [];
  let cursor = null;
  for (let page = 1; page <= maxPages; page += 1) {
    assertCurrent();
    let res;
    try {
      res = await pageQuery(supabase, userId, cursor, pageSize);
    } catch (err) {
      assertCurrent();
      throw new BetLoadError("page_error", `bets page ${page}: request failed: ${err?.message ?? err}`, { cause: err });
    }
    assertCurrent();
    const { data, error } = res || {};
    if (error) {
      const reason = EMBED_MISSING_CODES.has(error.code) ? "embed_missing" : "page_error";
      throw new BetLoadError(reason, `bets page ${page}: ${error.message || "error"}`, { code: error.code });
    }
    if (!Array.isArray(data)) throw new BetLoadError("non_array", `bets page ${page}: data is not an array`);
    // Only an EMPTY page ends the load: a short page is ambiguous when the server cap is below
    // pageSize (Q3).
    if (data.length === 0) return displayOrder(rows);
    for (const row of data) {
      if (!row || typeof row !== "object" || row.id == null || row.id === "") {
        throw new BetLoadError("row_shape", `bets page ${page}: a row has no id`);
      }
      const key = String(row.id);
      // A repeat means the gateway ignored the cursor or a page came back unordered: fail closed,
      // never loop and never duplicate (G5).
      if (seen.has(key)) throw new BetLoadError("cursor_stuck", `bets page ${page}: id seen twice`);
      seen.add(key);
      rows.push({ ...row, bet_legs: sortedLegs(row) });
    }
    cursor = data[data.length - 1].id; // as the server returned it; the next page's gt uses its collation
  }
  throw new BetLoadError("truncated", `bet load stopped at ${maxPages} pages of ${pageSize}`);
}
